import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useRef } from "react";
import { useField, useForm } from "react-final-form";

import {
  ActionButton,
  dataAttribute,
  DataAttributes,
  Heading,
  NoDataTile,
  Pivot,
  PivotItem,
  Stack
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ActiveConditionList } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/active-condition-list/ActiveConditionList.tsx";
import { appointmentFormNameOf } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/AppointmentForm.types.ts";
import {
  ConditionDetailLabels,
  ConditionModalFormValues,
  ConditionPivotHeader
} from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/condition-modal/Condition.types";
import { ConditionAppointmentFormHelper } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/condition-modal/ConditionAppointmentFormHelper.ts";
import { ConditionModal } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/condition-modal/ConditionModal.tsx";
import { ConditionContext } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/context/ConditionContext.ts";
import { ConditionHelper } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/context/ConditionHelper.ts";
import { SecondColumnWrapper } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/SecondColumnWrapper.tsx";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";

interface ChangeConditionSecondColumnProps {
  episodeOfCares: EpisodeOfCare[] | undefined;
  patientId: string | undefined;
  setCurrentEpisodeOfCare: (show: EpisodeOfCare) => void;
  currentEpisodeOfCare: EpisodeOfCare | undefined;
  encounter: Encounter;
}

export const ChangeConditionSecondColumn: React.FC<ChangeConditionSecondColumnProps> =
  observer(
    ({
      episodeOfCares,
      patientId,
      setCurrentEpisodeOfCare,
      currentEpisodeOfCare,
      encounter
    }) => {
      const root = useStores();

      const conditionsDischarged = episodeOfCares?.filter(
        x => x.discharged ?? false
      );

      const conditionsInProgress = episodeOfCares?.filter(
        x => !(x.discharged || x.claim?.isOnHold)
      );

      const conditionsOnHold = episodeOfCares?.filter(
        x => x.claim?.isOnHold ?? false
      );

      const showOnHoldCondition = root.core.hasPermissions(
        Permission.ClaimRead
      );

      const form = useForm<AppointmentFormValues>();

      const {
        input: { value: sendDemographicForm }
      } = useField(appointmentFormNameOf("sendDemographicForm"), {
        subscription: { value: true }
      });

      const updateForm = (episodeOfCare: EpisodeOfCare) => {
        form.batch(() => {
          form.change(
            appointmentFormNameOf("episodeOfCareId"),
            episodeOfCare.id
          );
          form.change(
            appointmentFormNameOf("sendAccForm"),
            episodeOfCare.sendAccForm
          );
          if (
            sendDemographicForm &&
            sendDemographicForm !== episodeOfCare.sendAccForm
          )
            form.change(
              appointmentFormNameOf("sendDemographicForm"),
              episodeOfCare.sendAccForm
            );
        });
      };

      const conditionAppointmentFormHelper = new ConditionAppointmentFormHelper(
        root,
        currentEpisodeOfCare
      );

      const conditionHelper = useRef<ConditionHelper>(
        new ConditionHelper(root, patientId ?? "", {
          onSubmit: async (
            values: ConditionModalFormValues,
            helper: ConditionHelper
          ) => {
            await helper.submitAddConditionValues(values, encounter.userId);
          },
          onSubmitSucceeded: async episodeOfCare => {
            runInAction(() => {
              conditionAppointmentFormHelper.conditionsPromise.set(
                Promise.resolve([episodeOfCare, ...(episodeOfCares ?? [])])
              );
            });

            conditionAppointmentFormHelper.setEpisodeOfCareId(episodeOfCare.id);
            //This is updating the AppointmentFormFields with values form the ClaimManagementModalForm once submitted
            updateForm(episodeOfCare);
          }
        })
      );

      const header = (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          grow={1}
        >
          <Heading
            variant="section-heading-light"
            {...dataAttribute(DataAttributes.Element, "second-column-heading")}
          >
            {ConditionDetailLabels.CurrentConditions}
          </Heading>
          <ActionButton
            iconProps={{ iconName: "add" }}
            onClick={() => conditionHelper.current.showConditionModal(false)}
            text={ConditionDetailLabels.New}
          />
        </Stack>
      );

      const defaultSelectedKey = (() => {
        if (currentEpisodeOfCare?.discharged)
          return ConditionPivotHeader.Discharged;
        if (currentEpisodeOfCare?.claim?.isOnHold)
          return ConditionPivotHeader.OnHold;
        return undefined;
      })();

      return (
        <ConditionContext.Provider value={conditionHelper.current}>
          <SecondColumnWrapper heading={header}>
            {episodeOfCares && episodeOfCares.length > 0 && patientId ? (
              <Stack styles={{ root: { width: "calc(100% - 2px)" } }}>
                <Pivot defaultSelectedKey={defaultSelectedKey}>
                  {conditionsInProgress && (
                    <PivotItem
                      itemCount={conditionsInProgress.length}
                      headerText={ConditionPivotHeader.InProgress}
                      itemKey={ConditionPivotHeader.InProgress}
                    >
                      <ActiveConditionList
                        episodesOfCare={conditionsInProgress}
                        setCurrentEpisodeOfCare={setCurrentEpisodeOfCare}
                        isFromChangeCondition={true}
                        setEpisodeOfCareIdForChangeCondtion={
                          conditionAppointmentFormHelper.setEpisodeOfCareId
                        }
                      />
                    </PivotItem>
                  )}
                  {conditionsDischarged && (
                    <PivotItem
                      itemCount={conditionsDischarged.length}
                      headerText={ConditionPivotHeader.Discharged}
                      itemKey={ConditionPivotHeader.Discharged}
                    >
                      <ActiveConditionList
                        episodesOfCare={conditionsDischarged}
                        setCurrentEpisodeOfCare={setCurrentEpisodeOfCare}
                        isFromChangeCondition={true}
                        setEpisodeOfCareIdForChangeCondtion={
                          conditionAppointmentFormHelper.setEpisodeOfCareId
                        }
                      />
                    </PivotItem>
                  )}
                  {showOnHoldCondition && conditionsOnHold && (
                    <PivotItem
                      itemCount={conditionsOnHold.length}
                      headerText={ConditionPivotHeader.OnHold}
                      itemKey={ConditionPivotHeader.OnHold}
                    >
                      <ActiveConditionList
                        episodesOfCare={conditionsOnHold}
                        setCurrentEpisodeOfCare={setCurrentEpisodeOfCare}
                        isFromChangeCondition={true}
                        setEpisodeOfCareIdForChangeCondtion={
                          conditionAppointmentFormHelper.setEpisodeOfCareId
                        }
                      />
                    </PivotItem>
                  )}
                </Pivot>
              </Stack>
            ) : (
              <NoDataTile
                styles={{ root: { width: "100%", justifyContent: "center" } }}
                textProps={{
                  text: ConditionDetailLabels.NoConditionsToDisplay
                }}
                greyView
                showBoxShadow={false}
                linkProps={{
                  text: ConditionDetailLabels.CreateNewCondition,
                  onClick: () => {
                    conditionHelper.current.showConditionModal(false);
                  }
                }}
              />
            )}
          </SecondColumnWrapper>
          <ConditionModal hideAddDiagnosisOption={true} />
        </ConditionContext.Provider>
      );
    }
  );
